import { Injectable } from "@angular/core";
import { getLoggerFor } from "@tylertech/logging";
import { Observable, Subject } from "rxjs";
import type { FoundryChatbotComponent } from "@ai-foundry/sdk/ui/foundry-chatbot";
import type { FoundrySidebarChatbotComponent } from "@ai-foundry/sdk/ui/foundry-sidebar-chatbot";
import { AIAgentRunSettleReason, LiveChatAdapter } from "../interfaces";

const FoundryRunStarterEvents = {
    PromptSend: "forge-ai-prompt-send",
    UserResend: "forge-ai-message-thread-user-resend",
    UserEdit: "forge-ai-message-thread-user-edit",
    ResponseRegenerate: "forge-ai-message-thread-resend",
    SuggestionSelect: "forge-ai-suggestions-select"
} as const;

const FoundryRunLifecycleEvents = {
    RunStarted: "foundry-chatbot-run-started",
    RunFinished: "foundry-chatbot-run-finished",
    RunAborted: "foundry-chatbot-run-aborted"
} as const;

type FoundryMessage = ReturnType<FoundrySidebarChatbotComponent["getMessages"]>[number];
type FoundryPromptSendEventDetail = HTMLElementEventMap[typeof FoundryRunStarterEvents.PromptSend]["detail"];

@Injectable()
export class FoundryChatAdapter implements LiveChatAdapter
{
    private readonly log = getLoggerFor("FoundryChatAdapter");

    private element: FoundrySidebarChatbotComponent;
    private liveModeActive = false;
    private savedPlaceholder: string;
    private endChatButton: HTMLElement;

    private readonly keyedMessageIds = new Map<string, string>();
    private readonly userTypedMessage$ = new Subject<string>();
    private readonly userTyping$ = new Subject<void>();
    private readonly liveModeChanged$ = new Subject<boolean>();

    get onUserTypedMessage(): Observable<string>
    {
        return this.userTypedMessage$.asObservable();
    }

    get onUserTyping(): Observable<void>
    {
        return this.userTyping$.asObservable();
    }

    get onLiveModeChanged(): Observable<boolean>
    {
        return this.liveModeChanged$.asObservable();
    }

    get isLive(): boolean
    {
        return this.liveModeActive;
    }

    attach(element: FoundrySidebarChatbotComponent): void
    {
        if (this.element === element)
            return;

        if (!element.shadowRoot)
            this.log.error("Foundry chatbot element has no shadow root - live chat rendering will not work.");

        this.element = element;
        for (const eventName of Object.values(FoundryRunStarterEvents))
            element.addEventListener(eventName, this.interceptRunStarter, { capture: true });
    }

    show(): void
    {
        this.element?.show();
    }

    whenAIAgentRunSettles(timeoutMs = 30000): Promise<AIAgentRunSettleReason>
    {
        return new Promise(resolve =>
        {
            const settle = (reason: AIAgentRunSettleReason): void =>
            {
                this.element.removeEventListener(FoundryRunLifecycleEvents.RunFinished, onFinished);
                this.element.removeEventListener(FoundryRunLifecycleEvents.RunAborted, onAborted);
                window.clearTimeout(timer);
                resolve(reason);
            };
            const onFinished = (): void => settle("finished");
            const onAborted = (): void => settle("aborted");
            const timer = window.setTimeout(() => settle("timeout"), timeoutMs);
            this.element.addEventListener(FoundryRunLifecycleEvents.RunFinished, onFinished);
            this.element.addEventListener(FoundryRunLifecycleEvents.RunAborted, onAborted);
        });
    }

    private readonly interceptRunStarter = (event: Event): void =>
    {
        if (!this.liveModeActive)
            return;

        event.stopPropagation();

        if (event.type === FoundryRunStarterEvents.PromptSend)
        {
            const text = (event as CustomEvent<FoundryPromptSendEventDetail>).detail.value.trim();
            this.appendClientOnlyMessage("user", text);
            this.userTypedMessage$.next(text);
        }
    };

    enterLiveMode(onEndChat: () => void): void
    {
        if (this.liveModeActive)
            return;

        this.liveModeActive = true;
        this.keyedMessageIds.clear();
        this.savedPlaceholder = this.element.placeholder;
        this.element.placeholder = "Message your support agent...";
        this.addEndChatButton(onEndChat);
        this.element.addEventListener("keydown", this.relayTyping);
        this.liveModeChanged$.next(true);
    }

    private readonly relayTyping = (): void =>
    {
        if (this.liveModeActive)
            this.userTyping$.next();
    };

    leaveLiveMode(): void
    {
        if (!this.liveModeActive)
            return;

        this.liveModeActive = false;
        this.element.removeEventListener("keydown", this.relayTyping);
        this.element.placeholder = this.savedPlaceholder;
        this.endChatButton?.remove();
        this.endChatButton = null;
        this.liveModeChanged$.next(false);
    }


    appendMessage(role: FoundryMessage["role"], content: string): void
    {
        this.writeTranscript([...this.element.getMessages(), this.buildMessage(role, content, false)]);
    }

    appendClientOnlyMessage(role: FoundryMessage["role"], content: string, replaceKey?: string): void
    {
        const removeId = replaceKey ? this.keyedMessageIds.get(replaceKey) : undefined;
        if (removeId)
            this.keyedMessageIds.delete(replaceKey);
        const current = this.element.getMessages().filter(message => message.id !== removeId);
        this.writeTranscript([...current, this.buildClientOnlyMessage(role, content)]);
    }

    upsertClientOnlyMessage(key: string, role: FoundryMessage["role"], content: string): void
    {
        const current = this.element.getMessages();
        const id = this.keyedMessageIds.get(key);
        const index = id ? current.findIndex(message => message.id === id) : -1;
        if (index < 0)
        {
            const message = this.buildClientOnlyMessage(role, content);
            this.keyedMessageIds.set(key, message.id);
            this.writeTranscript([...current, message]);
            return;
        }

        const messages = [...current];
        messages[index] = { ...messages[index], content, timestamp: Date.now() };
        this.writeTranscript(messages);
    }

    removeClientOnlyMessage(key: string): void
    {
        const id = this.keyedMessageIds.get(key);
        if (!id)
            return;

        this.keyedMessageIds.delete(key);
        const current = this.element.getMessages();
        if (current.some(message => message.id === id))
            this.writeTranscript(current.filter(message => message.id !== id));
    }

    private buildClientOnlyMessage(role: FoundryMessage["role"], content: string): FoundryMessage
    {
        return this.buildMessage(role, content, true);
    }

    private buildMessage(role: FoundryMessage["role"], content: string, clientOnly: boolean): FoundryMessage
    {
        return {
            id: `cx-${crypto.randomUUID()}`,
            role,
            content,
            timestamp: Date.now(),
            status: "complete",
            clientOnly
        };
    }

    private writeTranscript(messages: FoundryMessage[]): void
    {
        const inner = this.inner;
        if (!inner)
        {
            this.log.error("Inner foundry-chatbot element not found - transcript write dropped.");
            return;
        }
        void inner.setThreadState({ messages, timestamp: Date.now() });
    }

    private get inner(): FoundryChatbotComponent
    {
        return this.element?.shadowRoot?.querySelector<FoundryChatbotComponent>("foundry-chatbot") ?? null;
    }

    private addEndChatButton(onEndChat: () => void): void
    {
        const button = document.createElement("forge-button");
        button.slot = "header-actions";
        button.dense = true;
        button.textContent = "End chat";
        button.addEventListener("click", onEndChat);
        this.element.appendChild(button);
        this.endChatButton = button;
    }
}
